import React, { useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import Modal from 'components/common/modal-component';
import { ModalButton } from 'models/utils/common/modal-button-model';
import { setTOCRefresh, setTOCExpandAll } from 'store/actions/TOC-actions';
import TextArea from 'components/common/text-area-component';
import {
  fetchAllSectionRenderingData,
  fetchSectionIdListStatementContent,
  replaceSelectedSectionsContentPanel,
} from 'store/actions/statement-content-actions';
import { fetchStatementSummaryElements } from 'store/actions/statement-summary/elements-summary-actions';
import {
  getContentSectionMapRequest,
  getContentSectionMapOCRRequest,
} from 'store/actions/section-cache-actions';
import { fetchTheListOfNotes } from 'store/actions/notes-panel-actions';
import { fetchStatementSummarySections } from 'store/actions/statement-summary/sections-summary-actions';
import {
  updateSelectedSectionPanel,
  fetchSectionReviewsOCRWithLoading,
} from 'store/actions/sections-actions';
import { DEFAULT_ENTITY_STRING } from 'models/api/entity-list-model';
import {
  SECTION_HIGHLIGHT_STATES,
  NO_CLOSEST_BOOKMARKS_FOUND,
} from 'constants/feature/section-details-constants';
import {
  setCreateSectionAction,
  addNewSectionAction,
  removeNewSectionAction,
  setNewSectionIdAction,
  fetchSectionTreeAction,
} from 'store/actions/section-tree-list-actions';
import { SectionTreeListEntry } from 'models/data/section-tree-list-entry-model';
import { TooltipOptions } from 'models/utils/common/tooltip-options-model';
import ConditionalRenderComponent from 'components/util/conditional-render-component';
import {
  getBookmarkOrderNumber,
  checkIfExisitingBookmark,
} from 'utils/section-tree-utils';
import { addNavigationRequestOCR } from 'api/navigation-api';
import { FormattedMessage } from 'react-intl';
import KebabDropdown from 'components/common/dropdown-component';
import Radio from 'components/common/radio-component';
import { RadioOption } from 'models/utils/common/radio-options-model';
const ADD_NAVIGATOR_ID_BLOCK = 'add-navigator-modal-id';
const ADD_NAVIGATION_MODAL_BLOCK = 'add-navigator-modal';
const AddNavigationOCRModal = ({
  onClose,
  contextMenuData,
  sectionId,
  revision,
  clearSelection,
  entityList,
  setCreateSectionAction,
  contentSectionMap,
  addNewSectionAction,
  sectionIds,
  removeNewSectionAction,
  isOCR,
  replaceSelectedSectionsContentPanel,
  getContentSectionMapOCRRequest,
  updateSelectedSectionPanel,
  fetchSectionTreeAction,
  setTOCRefresh,
  setTOCExpandAll,
  sectionsHierarchy,
  fetchSectionReviewsOCRWithLoading,
}) => {
  const [contextData, setContextData] = useState(contextMenuData);
  const getSectionParentId = () => {
    const closestBookmark = contentSectionMap.getClosestBookmarkOCRSection({
      sectionId,
      selectedData: contextData,
    });
    return closestBookmark;
  };

  const _enableInlineButton = () => {
    const closestBookmark = getSectionParentId();
    if (closestBookmark === NO_CLOSEST_BOOKMARKS_FOUND) {
      return true;
    }
    const contentSectionMapFlattenArray = Object.values(contentSectionMap.map)
      ?.flatMap((sectionMap) => sectionMap)
      ?.filter((section) => !section?.deleted);

    const existingChildren = contentSectionMapFlattenArray.filter(
      (section) => section.parentId === closestBookmark.id,
    );

    const hasParentWithoutChild =
      (closestBookmark.parentId !== null || closestBookmark.parentId !== 0) &&
      existingChildren?.length === 0;

    const parentAndChildNotFound =
      (closestBookmark.parentId === null || closestBookmark.parentId === 0) &&
      existingChildren?.length === 0;

    return hasParentWithoutChild || parentAndChildNotFound;
  };

  const _enableIndentButton = () => {
    const closestBookmark = getSectionParentId();
    return closestBookmark !== NO_CLOSEST_BOOKMARKS_FOUND;
  };
  const [inlineInsertion, setInlineInsertion] = useState(_enableInlineButton());
  // Use a ref to keep track of the last parentId
  const lastParentIdRef = useRef(
    inlineInsertion ? getSectionParentId().parentId : getSectionParentId().id,
  );

  let uniqueId = 0;

  const [entity, setEntity] = useState(
    entityList.entitiesDropdownMap[DEFAULT_ENTITY_STRING],
  );

  const getNewSection = (parentId) => {
    const selectionText = contextData.selectedText;
    const section = new SectionTreeListEntry({
      children: null,
      id: parentId,
      closestBookmarkId: getSectionParentId()?.id,
      is_technical: null,
      name: selectionText,
      orderNum: 0,
      parentOrderNum: getSectionParentId()?.orderNum,
      parentSectionName: null,
      revisionId: revision.id,
      type: null,
      isNew: true,
    });
    return section;
  };

  useEffect(() => {
    setCreateSectionAction(true);
    const parentId =
      getSectionParentId() === NO_CLOSEST_BOOKMARKS_FOUND
        ? NO_CLOSEST_BOOKMARKS_FOUND
        : inlineInsertion
        ? getSectionParentId().parentId
        : getSectionParentId().id;

    // Remove previous new section if parentId changed
    if (
      lastParentIdRef.current !== null &&
      lastParentIdRef.current !== parentId
    ) {
      removeNewSectionAction({ sectionId: lastParentIdRef.current, isOCR });
    }
    removeNewSectionAction({ sectionId: parentId, isOCR });
    lastParentIdRef.current = parentId;

    addNewSectionAction({
      section: getNewSection(parentId),
      parentId,
      inline: inlineInsertion,
      isOCR,
    });
    // Ensure the section tree is expanded
    setTOCExpandAll(true);
    return () => {
      setCreateSectionAction(false);
      removeNewSectionAction({ sectionId: parentId, isOCR });
    };
  }, [inlineInsertion]);

  const _addNavigation = () => {
    const { preText, postText, selectedText, polygon } = contextData;
    const { containsBookmark, bookmarkContained } = checkIfExisitingBookmark({
      polygon,
      contentSectionMap,
      sectionId,
    });

    const existingBookmarkId = containsBookmark
      ? bookmarkContained.id
      : sectionIds.length > 0
      ? Math.max(...sectionIds)
      : 0;

    const orderNum = getBookmarkOrderNumber({
      sectionsHierarchy,
      contentSectionMap,
    });
    const payload = {
      containsBookmark,
      revisionId: revision.id,
      elementDetails: {
        polygon,
        preText: preText.trim(),
        postText: postText.trim(),
        sectionId,
        parentId:
          getSectionParentId() === NO_CLOSEST_BOOKMARKS_FOUND
            ? null
            : inlineInsertion
            ? getSectionParentId().parentId
            : getSectionParentId().id,
        existingBookmarkId,
        selectedText: selectedText.trim(),
        orderNum,
      },
    };

    onClose();
    addNavigationRequestOCR(payload).then(
      async ({
        data: {
          addedBookmark: { id },
        },
      }) => {
        fetchSectionReviewsOCRWithLoading();
        clearSelection();
        await getContentSectionMapOCRRequest({
          revisionId: revision.id,
          isOCR,
        });
        updateSelectedSectionPanel({
          sectionId,
          bookmarkId: id,
          isOCR,
        });
        setTOCRefresh(true);
        fetchSectionTreeAction({
          revisionId: revision.id,
          isOCR,
        });
        replaceSelectedSectionsContentPanel({
          color: SECTION_HIGHLIGHT_STATES.CREATED_NAV,
          sectionIds: [sectionId],
          bookmarkId: id,
        });
        setNewSectionIdAction(id);
      },
    );
  };

  const toolTipText = () => {
    if (!_enableInlineButton()) {
      return 'add-navigation-modal.options.inline-disabled';
    } else if (!_enableIndentButton()) {
      return 'add-navigation-modal.options.indented-disabled';
    }
  };

  const onCloseModal = () => {
    onClose();
    clearSelection();
  };

  return (
    <Modal
      id={ADD_NAVIGATOR_ID_BLOCK}
      title="add-navigator-modal.title"
      onClose={onCloseModal}
      primaryModalButton={
        new ModalButton({
          text: 'common.save',
          onClick: _addNavigation,
        })
      }
      secondaryModalButton={
        new ModalButton({
          text: 'common.cancel',
          onClick: onCloseModal,
        })
      }
    >
      <ConditionalRenderComponent dependencies={[entityList]}>
        <div className={`${ADD_NAVIGATION_MODAL_BLOCK}__form-item`}>
          <TextArea
            id={`${ADD_NAVIGATION_MODAL_BLOCK}__form-item-id`}
            label="add-navigation-modal.form.heading.label"
            isValid={true}
            placeholder="add-navigation-modal.form.label.placeholder"
            value={contextData.selectedText}
            width="100%"
            rows={3}
            onChange={(e) => {
              setContextData({ ...contextData, selectedText: e });
            }}
          />
        </div>
        <div className={`${ADD_NAVIGATION_MODAL_BLOCK}__form-item`}>
          <div className={'form-item__label'}>
            <FormattedMessage id="add-navigation-modal.form.entity.label" />
          </div>
          <KebabDropdown
            className={'entity-type__kebab'}
            menuClassName={'entity__kebab-menu'}
            id={'project-kebab'}
            options={Object.values(entityList.entitiesDropdownMap)}
            width={'100%'}
            onClickFormItem={(e) => {
              e.stopPropagation();
            }}
            value={entity}
            onSelectOption={(option) => {
              setEntity(option);
            }}
          />
        </div>
        <div className={`${ADD_NAVIGATION_MODAL_BLOCK}__form-item`}>
          <p className={`${ADD_NAVIGATION_MODAL_BLOCK}__instructions`}>
            <FormattedMessage id="add-navigator-modal.static-text" />
          </p>
          <Radio
            id={'navigation-insertion-type'}
            className={'navigation-insertion__radio'}
            tooltip={
              toolTipText() &&
              new TooltipOptions({
                text: {
                  id: toolTipText(),
                  values: {
                    br: (
                      <br
                        key={`${ADD_NAVIGATOR_ID_BLOCK}-brTag-${++uniqueId}`}
                      />
                    ),
                  },
                },
                id: 'add-navigation-modal.options.tooltip-id',
                position: 'right',
                className: `${ADD_NAVIGATOR_ID_BLOCK}__tooltip`,
              })
            }
            options={[
              new RadioOption({
                id: 'add-navigation-ocr-modal.options.inline-id',
                label: 'add-navigation-modal.options.inline',
                value: true,
                disabled: !_enableInlineButton(),
                tooltipEnabled: !_enableInlineButton(),
              }),
              new RadioOption({
                id: 'add-navigation-ocr-modal.options.indented-id',
                label: 'add-navigation-modal.options.indented',
                value: false,
                disabled: !_enableIndentButton(),
                tooltipEnabled: !_enableIndentButton(),
              }),
            ]}
            width="auto"
            isValid
            selectedOption={inlineInsertion}
            onChange={(val) => {
              setInlineInsertion(val);
            }}
          />
        </div>
      </ConditionalRenderComponent>
    </Modal>
  );
};
const mapStateToProps = ({
  data: {
    sectionTreeList: { sectionsHierarchy },
    selectedStatement: { isOCR },
    elementPanel: { entityList },
    sectionTreeList,
    statementContent: {
      sectionsCache: { contentSectionMap },
      sectionIdListOcr: {
        data: { sectionIds },
      },
    },
    revision,
  },
  ui: {
    statementPage: { panels, sectionsInView },
  },
  sockets: {
    statementSocket: { socketHasBeenDisconnected },
  },
}) => ({
  sectionTreeList,
  entityList,
  revision,
  contentSectionMap,
  panels,
  socketHasBeenDisconnected,
  sectionsInView,
  sectionIds,
  sectionsHierarchy,
  isOCR,
});
const mapDispatchToProps = {
  setTOCRefresh,
  fetchStatementSummaryElements,
  fetchAllSectionRenderingData,
  getContentSectionMapRequest,
  getContentSectionMapOCRRequest,
  fetchSectionIdListStatementContent,
  fetchTheListOfNotes,
  fetchStatementSummarySections,
  updateSelectedSectionPanel,
  setCreateSectionAction,
  addNewSectionAction,
  removeNewSectionAction,
  setTOCExpandAll,
  setNewSectionIdAction,
  fetchSectionTreeAction,
  replaceSelectedSectionsContentPanel,
  fetchSectionReviewsOCRWithLoading,
};
export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(AddNavigationOCRModal);
